import { useEffect, useCallback, useState, useMemo } from 'react'
import { studentsStore } from '../model/studentsStore'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg'
import { ReactComponent as FilterIcon } from 'assets/icons/filter_empty.svg'
import { studentsFiltersStore } from '../model/studentsFiltersStore'
import { ensureStudentsLoaded } from '../model/ensureStudentsLoaded'

export const useStudentsPage = () => {
	const [isSendModalOpen, setIsSendModalOpen] = useState(false)
	const [isAssignActivityOpen, setIsAssignActivityOpen] = useState(false)
	// const [isBookSessionOpen, setBookSessionOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		let alive = true

		const load = async () => {
			try {
				setIsLoading(true)
				setError(null)
				await ensureStudentsLoaded()
			} catch (e) {
				console.error(e)
				if (alive) setError(e?.message || 'Failed to load students')
			} finally {
				if (alive) setIsLoading(false)
			}
		}

		const needsAvatarRefresh = (studentsStore.students || []).some(
			s => !s?.avatarKey,
		)
		if (!studentsStore.students?.length || needsAvatarRefresh) {
			load()
		} else {
			setIsLoading(false)
		}

		return () => {
			alive = false
		}
	}, [])

	const openSendModal = useCallback(() => setIsSendModalOpen(true), [])
	const closeSendModal = useCallback(() => setIsSendModalOpen(false), [])

	const openAssignActivityModal = useCallback(
		() => setIsAssignActivityOpen(true),
		[],
	)
	const closeAssignActivityModal = useCallback(
		() => setIsAssignActivityOpen(false),
		[],
	)

	// const openBookSessionModal = useCallback(() => setBookSessionOpen(true), [])
	// const closeBookSessionModal = useCallback(() => setBookSessionOpen(false), [])

	const setGrade = useCallback(id => {
		studentsStore.setGrade(id)
	}, [])

	// Access studentsStore.students directly inside useMemo to make this reactive to MobX changes
	// Since the component is wrapped in observer, accessing this will trigger re-renders
	const { allItems, gradeTabs } = useMemo(() => {
		const students = studentsStore.students || []
		if (students.length === 0) {
			return {
				allItems: 0,
				gradeTabs: [],
			}
		}
		const gradeCounts = {}

		students.forEach(s => {
			const grab = Number(s?.grade)
			if (grab && !isNaN(grab) && grab > 0) {
				gradeCounts[grab] = (gradeCounts[grab] || 0) + 1
			}
		})

		const gradeTabs = Object.entries(gradeCounts)
			.sort((a, b) => Number(a[0]) - Number(b[0]))
			.map(([grade, count]) => ({
				id: `grade-${grade}`,
				label: `Grade ${grade}`,
				count,
			}))

		return {
			allItems: students.length,
			gradeTabs,
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [studentsStore.students.length])

	const tabs = useMemo(() => {
		// Default tabs (cannot be removed)
		const baseTabs = [
			{ id: 'all', label: 'All Students', count: allItems },
			...gradeTabs,
		]

		// Dynamic tabs from saved student lists (API + local)
		// const listTabs =
		// 	(studentsStore.savedLists || []).map(list => ({
		// 		id: `list:${list.id}`,
		// 		label: list.name || 'Saved List',
		// 		// Use length of studentIds if available, otherwise omit count
		// 		count: Array.isArray(list.studentIds) ? list.studentIds.length : null,
		// 		isListTab: true,
		// 		listId: list.id,
		// 	})) || []

		return baseTabs
		// return [...baseTabs, ...listTabs]
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allItems, gradeTabs])

	const actions = useMemo(() => {
		return [
			{
				id: 'filter',
				icon: <FilterIcon />,
				onClick: () => studentsFiltersStore.toggle(),
			},
			{
				id: 'settings',
				icon: <SettingsIcon />,
				typeIconHover: 'stroke',
				onClick: () => {},
			},
		]
	}, [])

	const closeFilter = useCallback(() => {
		studentsFiltersStore.close()
	}, [])

	return {
		activeGrade: studentsStore.activeGrade,
		hasSelection: studentsStore.hasSelection,
		selectedStudents: studentsStore.selectedStudents,
		// savedLists: studentsStore.savedLists,
		// applySavedList: studentsStore.applySavedList,
		isSendModalOpen,
		openSendModal,
		closeSendModal,
		setGrade,
		tabs,
		actions,
		isOpenFilter: studentsFiltersStore.isOpen,
		closeFilter,
		isAssignActivityOpen,
		openAssignActivityModal,
		closeAssignActivityModal,
		// isBookSessionOpen,
		// openBookSessionModal,
		// closeBookSessionModal,
		isLoading,
		error,
	}
}
